import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import './InfoSection.css';

const InfoSection = ({
  title,
  description,
  image,
  imagePosition = 'right',
  backgroundColor,
  icon: Icon,
  iconColor = '#1B422E',
  titleColor = '#1B422E',
  textColor = '#505050',
  maxWidth = 'lg',
  spacing = 4,
  imageSize = 'medium' // small, medium, large
}) => {
  // Handle description as string or array
  const descriptionContent = Array.isArray(description)
    ? description
    : [description];

  // Image size mapping
  const imageSizeMap = {
    small: { xs: '200px', md: '300px' },
    medium: { xs: '250px', md: '400px' },
    large: { xs: '300px', md: '500px' }
  };

  const imageMaxWidth = imageSizeMap[imageSize] || imageSizeMap.medium;

  // Determine if image should be on left or right
  const imageOnLeft = imagePosition === 'left';
  const hasImage = image && imagePosition !== 'none';

  return (
    <Box
      className="info-section"
      sx={{
        backgroundColor: backgroundColor || 'transparent',
        py: { xs: 4, md: 6 }
      }}
    >
      <Container maxWidth={maxWidth}>
        <Grid
          container
          spacing={spacing}
          alignItems="center"
          direction={imageOnLeft ? 'row-reverse' : 'row'}
          sx={{
            flexDirection: {
              xs: 'column-reverse',
              md: imageOnLeft ? 'row-reverse' : 'row'
            }
          }}
        >
          {/* Text Content */}
          <Grid item xs={12} md={hasImage ? 6 : 12}>
            <Box className="info-content">
              {/* Icon */}
              {Icon && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Icon
                    sx={{
                      fontSize: { xs: 40, md: 50 },
                      color: iconColor
                    }}
                  />
                </Box>
              )}

              {/* Title */}
              <Typography
                variant="h2"
                sx={{
                  fontFamily: '"Reem Kufi", sans-serif',
                  fontSize: {
                    xs: 'clamp(1.75rem, 4vw, 2.25rem)',
                    md: 'clamp(2rem, 4vw, 2.75rem)'
                  },
                  fontWeight: 700,
                  color: titleColor,
                  mb: 2,
                  lineHeight: 1.3
                }}
              >
                {title}
              </Typography>

              {/* Description */}
              {descriptionContent.map((paragraph, index) => (
                <Typography
                  key={index}
                  variant="body1"
                  sx={{
                    fontFamily: '"Reem Kufi", sans-serif',
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    fontWeight: 400,
                    color: textColor,
                    lineHeight: 1.7,
                    mb: index < descriptionContent.length - 1 ? 2 : 0
                  }}
                >
                  {paragraph}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Image */}
          {hasImage && (
            <Grid item xs={12} md={6}>
              <Box
                className="info-image"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <img
                  src={image}
                  alt={title}
                  style={{
                    width: '100%',
                    maxWidth: imageMaxWidth.md,
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default InfoSection;
